import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { signOut, resetPassword } from '../../firebase/auth';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Bars3Icon } from '@heroicons/react/24/outline';
import LanguageChip from '../shared/LanguageChip';
import HelpButton from '../help/HelpButton';

function getInitials(displayName, email) {
  if (displayName) return displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (email?.[0] ?? '?').toUpperCase();
}

export default function Topbar({ title, onMenuClick }) {
  const { t } = useTranslation();
  const { user, userProfile, isGuardian, access } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [resetState, setResetState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const canSwitchPortal = isGuardian && Array.isArray(access) && access.length > 1;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleResetPassword = async () => {
    if (!user?.email || resetState === 'sending') return;
    setResetState('sending');
    try {
      await resetPassword(user.email);
      setResetState('sent');
      setTimeout(() => { setResetState('idle'); setMenuOpen(false); }, 2500);
    } catch {
      setResetState('idle');
      toast(t('common:actions.couldNotSendReset'));
    }
  };

  const initials = getInitials(userProfile?.displayName, userProfile?.email);

  return (
    <header
      className="app-topbar px-4 md:px-7"
      style={{
        height: 52,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <button
          className="md:hidden flex items-center justify-center"
          onClick={onMenuClick}
          style={{
            width: 36, height: 36, borderRadius: 7, border: 'none',
            background: 'transparent', cursor: 'pointer',
            color: 'var(--text-1)', flexShrink: 0,
          }}
        >
          <Bars3Icon style={{ width: 20, height: 20 }} />
        </button>
        <h1
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-1)',
            letterSpacing: '-0.02em',
          }}
        >
          {t(title)}
        </h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <HelpButton />
        <LanguageChip />
        <div style={{ position: 'relative' }}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 8px',
            borderRadius: 7,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--accent)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {initials}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-1)', display: 'none' }}
            className="sm:block"
          >
            {userProfile?.displayName || userProfile?.email}
          </span>
          <ChevronDownIcon style={{ width: 14, height: 14, color: 'var(--text-3)' }} />
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div
              style={{
                position: 'absolute',
                right: 0,
                marginTop: 6,
                width: 160,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                zIndex: 20,
                padding: '4px',
              }}
            >
              {canSwitchPortal && (
                <button
                  onClick={() => navigate('/portal/choose')}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontSize: 12.5,
                    color: 'var(--text-1)',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {t('common:actions.switchPortal')}
                </button>
              )}
              <button
                onClick={handleResetPassword}
                disabled={resetState === 'sending'}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 12.5,
                  color: resetState === 'sent' ? 'var(--accent)' : 'var(--text-1)',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: resetState === 'sending' ? 'wait' : 'pointer',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {resetState === 'sending'
                  ? t('common:actions.sendingEllipsis')
                  : resetState === 'sent'
                  ? t('common:actions.emailSent')
                  : t('common:actions.resetPassword')}
              </button>
              <button
                onClick={handleSignOut}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 12.5,
                  color: 'var(--text-1)',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {t('common:actions.signOut')}
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </header>
  );
}
